import { schema } from '@edgevanta/db/drizzle/client';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';
import { z } from 'zod/v4';

export const selectProposalSchema = createSelectSchema(schema.proposals);

export const insertProposalSchema = createInsertSchema(schema.proposals, {
  callNumber: z.string(),
  completionDate: z.date().nullable().optional(),
  completionCalendarDays: z.number().nullable().optional(),
  completionWorkingDays: z.number().nullable().optional(),
  contractId: z.string(),
  contractType: z.string(),
  projectNumber: z.string().nullable().optional(),
  description: z.string(),
  districtCode: z.string(),
  districtName: z.string(),
  lettingDate: z.date(),
  lettingId: z.string().uuid(),
  ownerId: z.string().uuid(),
  workType: z.string(),
  route: z.string(),
  hasAwarded: z.boolean(),
  hasBidTabulation: z.boolean(),
});

export const updateProposalSchema = insertProposalSchema.partial().extend({
  id: z.string(),
});

const ProposalsQuerySchema = z.object({
  search: z.string().optional(),
});

const csvUuidArraySchema = z.preprocess((val) => {
  const values = Array.isArray(val)
    ? val.flatMap((item) =>
        typeof item === 'string' ? item.split(',').filter(Boolean) : item,
      )
    : typeof val === 'string'
      ? val.split(',').filter(Boolean)
      : undefined;

  if (!values || values.length === 0) {
    return undefined;
  }

  return values;
}, z.array(z.uuid()).optional());

export const getProposalsQuerySchema = ProposalsQuerySchema.merge(
  z.object({
    limit: z.coerce.number().optional().default(25),
    offset: z.coerce.number().optional().default(0),
    page: z.coerce.number().optional(),
    lettingDate: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }
        return val.split(',').map((num) => Number(num));
      })
      .default([]),
    lettingId: z.uuid().optional(),
    // Owner narrowing — agent surface resolves "Florida DOT" → ownerIds[]
    // and passes the array so we do ONE IN-clause query, not a call per
    // owner. UI keeps the singular `ownerId` path (same FK-indexed plan).
    ownerId: z.uuid().optional(),
    ownerIds: csvUuidArraySchema,
    // URL-facing multi-owner filter used by cross-state proposal search.
    owners: csvUuidArraySchema,
    createdAt: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }

        return val.split(',').map((num) => Number(num));
      })
      .default([]),
    costEstimate: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }
        return val.split(',').map((num) => (num ? Number(num) : undefined));
      })
      .default([]),
    completionDate: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }
        return val.split(',').map((num) => Number(num));
      })
      .default([]),
    county: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }
        return val.split(',');
      })
      .default([]),
    district: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [];
        }
        return val.split(',');
      })
      .default([]),
    sort: z
      .string()
      .optional()
      .transform((val) => {
        if (!val) {
          return [
            {
              id: 'callNumber',
              desc: false,
            },
          ];
        }

        try {
          const parsed = JSON.parse(val);
          const sortSchema = z.array(
            z.object({
              id: z.string(),
              desc: z.boolean(),
            }),
          );

          const result = sortSchema.safeParse(parsed);

          if (!result.success) {
            return [
              {
                id: 'callNumber',
                desc: false,
              },
            ];
          }

          return result.data;
        } catch {
          return [
            {
              id: 'callNumber',
              desc: false,
            },
          ];
        }
      })
      .default([
        {
          id: 'callNumber',
          desc: false,
        },
      ]),
  }),
);

export const getProposalsCountQuerySchema = ProposalsQuerySchema;
